import { useState } from 'react';

import {
  composeValue,
  decomposeValue,
  type SegmentOptions,
  type SegmentValues,
  type TemporalKind,
} from './segments';

export interface SegmentStateOptions {
  kind: TemporalKind;
  value: string | null | undefined;
  defaultValue: string | undefined;
  onChange: ((value: string) => void) | undefined;
  options: SegmentOptions;
}

export interface SegmentState {
  /** What the segments show; may be partial. */
  segments: SegmentValues;
  /** The value the field reports: the native string, empty while partial. */
  value: string;
  /** Sets the segments and reports the value when it changed. */
  update: (next: SegmentValues) => void;
}

/**
 * Keeps the segments (which may be partial) and the native value string in step. A value from
 * the parent that differs from the last one reported replaces the segments; the parent echoing
 * back what the field reported (an empty string while partial) leaves them alone, so a half-typed
 * date survives the round trip through react-hook-form.
 */
export function useSegmentState({
  kind,
  value,
  defaultValue,
  onChange,
  options,
}: SegmentStateOptions): SegmentState {
  const controlled = value !== undefined;
  const [uncontrolled, setUncontrolled] = useState(defaultValue ?? '');
  const current = (controlled ? value : uncontrolled) ?? '';
  const [segments, setSegments] = useState(() => decomposeValue(kind, current, options));
  const [synced, setSynced] = useState({ value: current, hour12: options.hour12 });

  // A new value from outside (reset, setValue, another control), or a switch of clock.
  if (synced.value !== current || synced.hour12 !== options.hour12) {
    setSynced({ value: current, hour12: options.hour12 });
    setSegments(decomposeValue(kind, current, options));
  }

  const update = (next: SegmentValues) => {
    setSegments(next);
    const nextValue = composeValue(kind, next, options);
    if (nextValue === synced.value) return;
    setSynced({ value: nextValue, hour12: options.hour12 });
    if (!controlled) setUncontrolled(nextValue);
    onChange?.(nextValue);
  };

  return { segments, value: current, update };
}
